import React, { useState, useEffect } from "react";
import _ from "lodash";
import {
  _findSelectOption,
  _getBaseUrl,
  _capitalized,
  _isFirstRender,
  _getSkipByPage,
  _getCSRFTokenValue,
  _getTranslation,
  _getPlaceHolderKey,
} from "../../utils/helper";
import api from "../../utils/api";

import { Loader, Pagination } from "../../atoms";
import { CardPublication } from "../../cards";
import { InputSearch, InputSelect, MobileFilters } from "../../forms";

const INITIAL_VALUES = {
  region: "",
  searchTerm: "",
  page: 1,
};

const PublicationsByThemes = (props) => {
  const [values, setValues] = useState(INITIAL_VALUES);
  const [themeNid, setThemeNid] = useState("");
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_VALUES);
  const [posts, setPosts] = useState([]);
  const [filterOptions, setFilterOptions] = useState([]);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const isFirstLoad = _isFirstRender();
  const language = props?.data?.language;

  useEffect(() => {
    if (props?.data) {
      const theme_nid = props?.data?.nid ? parseInt(props?.data?.nid) : "";
      setThemeNid(theme_nid);
    }
  }, [props?.data]);

  useEffect(() => {
    if (themeNid !== "") {
      fetchFilters();
      fetchPosts();
    }
  }, [themeNid]);

  useEffect(() => {
    // sync the values when filters are applied
    setValues(appliedFilters);

    if (!isFirstLoad) {
      const debouncedFetchPosts = setTimeout(() => {
        fetchPosts();
      }, 500);
      return () => clearTimeout(debouncedFetchPosts);
    }
  }, [appliedFilters]);

  const fetchFilters = () => {
    setIsLoadingFilters(true);
    api
      .get(`/publication/getregionsbytheme?theme_nid=${themeNid}&lang=${language}`)
      .then((res) => {
        setIsLoadingFilters(false);
        const filtersData = res.data;
        const formattedList = [
          {
            label: "Region",
            key: "region",
            options: filtersData,
          },
        ];
        setFilterOptions(formattedList);
      })
      .catch(() => {
        setIsLoadingFilters(false);
      });
  };

  const fetchPosts = async () => {
    setIsLoadingPosts(true);

    const POSTS_PER_PAGE = 8;
    const POSTS_SKIP = _getSkipByPage(appliedFilters.page, POSTS_PER_PAGE);

    const fields = {
      date: "",
      country: "",
      type: "",
      theme: themeNid,
      region: appliedFilters.region,
      searchTerm: appliedFilters.searchTerm,
      skip: POSTS_SKIP,
      take: POSTS_PER_PAGE,
    };

    const csrfToken = await _getCSRFTokenValue();
    api
      .post("/publication/basicsearch", fields, {
        headers: {
          "X-CSRF-Token": csrfToken,
        },
      })
      .then((res) => {
        const TOTAL_POSTS = res.data.totalNodes;
        const TOTAL_PAGES = Math.ceil(TOTAL_POSTS / POSTS_PER_PAGE);

        setIsLoadingPosts(false);
        setPosts(res.data.publications);
        setTotalPages(TOTAL_PAGES);
      })
      .catch(() => {
        setIsLoadingPosts(false);
      });
  };

  const FilterInputs = () => {
    return isLoadingFilters ? (
      <Loader />
    ) : (
      <React.Fragment>
        <div className="filter-item">
          {filterOptions.map((item) => (
            <div className="flex items-center gap-x-4">
              <label className="hidden sm:block">{`${_getTranslation(item.key)}`}</label>
              <InputSelect
                class="w-full"
                name={item.key}
                placeholder={`${_getTranslation(_getPlaceHolderKey(item.key))}`}
                options={item.options}
                isClearable
                isSearchable
                value={_findSelectOption(item.options, values[item.key])}
                onChange={(name, selectedValues) => {
                  setValues({
                    ...values,
                    [item.key]: selectedValues,
                  });
                }}
              />
            </div>
          ))}
        </div>

        <div className="grid sm:grid-cols-4 gap-y-2 gap-x-4 sm:col-span-2 lg:col-span-3 xl:col-span-2 sm:mt-4 xl:mt-0">
          <button className="btn btn-s--pill" onClick={() => applyFilters()}>
            {_getTranslation("apply_filters")}
          </button>
          {!_.isEqual(values, INITIAL_VALUES) && (
            <button
              className="btn btn-s--pill btn-c--hollow---black"
              onClick={() => resetFilters()}
            >
              {_getTranslation("reset_filters")}
            </button>
          )}
          {themeNid === 509 && (
            <div className="hidden sm:block self-center col-span-2 ml-4">
              <ConstitutionNetCta />
            </div>
          )}
        </div>
      </React.Fragment>
    );
  };

  const applyFilters = (pageNo = 1) => {
    setAppliedFilters({ ...values, page: pageNo });
  };

  const resetFilters = () => {
    setValues(INITIAL_VALUES);
    setAppliedFilters(INITIAL_VALUES);
  };

  const ConstitutionNetCta = () => {
    return (
      <a
        className="tema-text--link---arrow"
        href="https://constitutionnet.org/thematic-publications"
        target="_blank"
      >
        {_getTranslation("browse_thematically_on_constitutionnet")}
      </a>
    );
  };

  return (
    <div className="container">
      <h2 className="h2 !mb-4">{_getTranslation("publications")}</h2>

      <div className="desktop-filters hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-y-2 gap-x-4 sm:gap-x-8 mb-8">
        <div className="filter-item">
          <InputSearch
            class="w-full"
            placeholder={_getTranslation("type_in_your_keywords")}
            value={values.searchTerm}
            onChange={(e) => {
              const value = e.target.value;
              setValues({ ...values, searchTerm: value });
            }}
          />
        </div>
        <FilterInputs />
      </div>

      <MobileFilters class="w-full sm:hidden mb-6">
        <div className="filter-item">
          <InputSearch
            class="w-full"
            placeholder={_getTranslation("type_in_your_keywords")}
            value={values.searchTerm}
            onChange={(e) => {
              const value = e.target.value;
              setValues({ ...values, searchTerm: value });
            }}
          />
        </div>
        <FilterInputs />
      </MobileFilters>

      {themeNid === 509 && (
        <div className="sm:hidden mb-8">
          <ConstitutionNetCta />
        </div>
      )}

      {isLoadingPosts && <Loader class="mb-8" />}
      {_.isArray(posts) && posts.length > 0 ? (
        <React.Fragment>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8 mb-8">
            {posts.map((post) => (
              <CardPublication data={post} />
            ))}
          </div>

          <div className="flex items-center justify-center gap-x-6 mt-8">
            <Pagination
              page={appliedFilters.page}
              totalPages={totalPages}
              setPage={(newPage) => {
                applyFilters(newPage);
              }}
            />
            {isLoadingPosts && <Loader />}
          </div>
        </React.Fragment>
      ) : isLoadingPosts ? null : (
        <p>{_getTranslation("there_are_no_posts_available")}.</p>
      )}
    </div>
  );
};

export default PublicationsByThemes;
